import {
  ArrowTopRightOnSquareIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { Check } from 'lucide-react';
import React from 'react';

import { TabOption } from '@/components/chariot-ui';
import { CopyToClipboard } from '@/components/CopyToClipboard';
import { MarkdownPreview } from '@/components/markdown/MarkdownPreview';
import {
  RENDERED_EXPLOIT_KEYS,
  RENDERED_FINDING_KEYS,
} from '@/sections/poe/constants/renderedKeys';
import type {
  ConstantinePOE,
  MainTabKey,
  POEContent,
  SectionContent,
} from '@/sections/poe/types/poe.types';
import { cn } from '@/utils/classname';
import { safeExecute } from '@/utils/function.util';

export const processConstantineDataSource = (
  file: POEContent | string,
  sections: Record<string, SectionContent>,
  tempTabs: Record<MainTabKey, TabOption[]>
) => {
  const constantineData = safeExecute(() => {
    return typeof file === 'string' ? JSON.parse(file) : file;
  }) as ConstantinePOE | null;

  if (!constantineData) return;

  const { findings = [], exploit_results = [] } = constantineData;

  // Return early if no findings to display
  if (findings.length === 0) return;

  // Create individual finding tabs grouped by severity
  const sortedFindings = [...findings].sort((a, b) => {
    const severityOrder = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3, INFO: 4 };
    const aOrder =
      severityOrder[a.severity as keyof typeof severityOrder] ?? 99;
    const bOrder =
      severityOrder[b.severity as keyof typeof severityOrder] ?? 99;
    if (aOrder !== bOrder) return aOrder - bOrder;
    const aConf = typeof a.confidence === 'number' ? a.confidence : 0;
    const bConf = typeof b.confidence === 'number' ? b.confidence : 0;
    return bConf - aConf;
  });

  sortedFindings.forEach((finding, index) => {
    const {
      id,
      title,
      severity,
      cwe,
      file_path,
      line_range,
      function_name,
      confidence,
      description,
      code_snippet,
      remediation,
      cvss_score,
      cvss_vector,
      verification,
    } = finding;

    const confidenceDisplay =
      typeof confidence === 'number'
        ? `${Math.round(confidence * 100)}%`
        : String(confidence);

    const tabId = `constantine_${id.toLowerCase()}_${index}`;
    const exploit = exploit_results?.find(er => er.id === id);

    sections[tabId] = (
      <div className="space-y-4">
        {/* Finding metadata */}
        <div className="border-chariot-stroke overflow-hidden rounded-lg border">
          <div className="bg-chariot-background-secondary flex items-center justify-end gap-2 px-4 py-3">
            {exploit?.success && (
              <span className="bg-chariot-status-critical border-chariot-stroke-critical inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium text-white">
                <ExclamationTriangleIcon className="mr-1 size-3" />
                Exploited
              </span>
            )}
            <span
              className={cn(
                'inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium text-white',
                severity === 'CRITICAL'
                  ? 'bg-chariot-status-critical border-chariot-stroke-critical'
                  : severity === 'HIGH'
                    ? 'bg-chariot-status-high border-chariot-stroke-high'
                    : severity === 'MEDIUM'
                      ? 'bg-chariot-status-medium border-chariot-stroke-medium'
                      : 'bg-chariot-status-low border-chariot-stroke-low'
              )}
            >
              {severity} Severity
            </span>
            <span
              className={cn(
                'inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium',
                typeof confidence === 'number' && confidence >= 0.9
                  ? 'bg-chariot-status-critical border-chariot-stroke-critical text-white'
                  : typeof confidence === 'number' && confidence >= 0.7
                    ? 'bg-chariot-status-high border-chariot-stroke-high text-white'
                    : typeof confidence === 'number' && confidence >= 0.4
                      ? 'bg-chariot-status-medium border-chariot-stroke-medium text-white'
                      : 'bg-chariot-status-low border-chariot-stroke-low text-white'
              )}
            >
              {confidenceDisplay} Confidence
            </span>
          </div>

          <table className="w-full">
            <tbody>
              <tr className="border-chariot-stroke border-b">
                <td className="bg-chariot-background-primary text-chariot-text-secondary w-1/5 px-4 py-3 font-medium">
                  CWE
                </td>
                <td className="text-chariot-text-primary px-4 py-3">
                  <CopyToClipboard alwaysShowCopyIcon textToCopy={cwe}>
                    <a
                      href={`https://cwe.mitre.org/data/definitions/${cwe.replace('CWE-', '')}.html`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-chariot-stroke-blue inline-flex items-center gap-1 hover:opacity-80"
                    >
                      <span className="font-mono">{cwe}</span>
                      <ArrowTopRightOnSquareIcon className="size-3" />
                    </a>
                  </CopyToClipboard>
                </td>
              </tr>
              {(cvss_score != null || cvss_vector) && (
                <tr className="border-chariot-stroke border-b">
                  <td className="bg-chariot-background-primary text-chariot-text-secondary w-1/5 px-4 py-3 font-medium">
                    CVSS
                  </td>
                  <td className="text-chariot-text-primary px-4 py-3">
                    <div className="flex items-center gap-2">
                      {cvss_score != null && (
                        <span
                          className={cn(
                            'inline-flex items-center rounded-md border px-2 py-1 text-xs font-bold',
                            cvss_score >= 9.0
                              ? 'bg-chariot-status-critical border-chariot-stroke-critical text-white'
                              : cvss_score >= 7.0
                                ? 'bg-chariot-status-high border-chariot-stroke-high text-white'
                                : cvss_score >= 4.0
                                  ? 'bg-chariot-status-medium border-chariot-stroke-medium text-white'
                                  : 'bg-chariot-status-low border-chariot-stroke-low text-white'
                          )}
                        >
                          {cvss_score}
                        </span>
                      )}
                      {cvss_vector && (
                        <code className="text-chariot-text-secondary font-mono text-xs">
                          {cvss_vector}
                        </code>
                      )}
                    </div>
                  </td>
                </tr>
              )}
              <tr className="border-chariot-stroke border-b">
                <td className="bg-chariot-background-primary text-chariot-text-secondary w-1/5 px-4 py-3 font-medium">
                  File
                </td>
                <td className="text-chariot-text-primary px-4 py-3">
                  <CopyToClipboard alwaysShowCopyIcon textToCopy={file_path}>
                    <code className="bg-chariot-background-secondary rounded px-2 py-1 font-mono text-sm">
                      {file_path}
                    </code>
                  </CopyToClipboard>
                </td>
              </tr>
              {line_range && (
                <tr className="border-chariot-stroke border-b">
                  <td className="bg-chariot-background-primary text-chariot-text-secondary w-1/5 px-4 py-3 font-medium">
                    Location
                  </td>
                  <td className="text-chariot-text-primary px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-chariot-text-secondary text-sm">
                        Lines
                      </span>
                      <code className="bg-chariot-background-secondary rounded px-2 py-1 font-mono text-sm">
                        {line_range}
                      </code>
                      {function_name && (
                        <>
                          <span className="text-chariot-text-secondary text-sm">
                            in
                          </span>
                          <code className="bg-chariot-background-secondary rounded px-2 py-1 font-mono text-sm">
                            {function_name}
                          </code>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )}
              <tr className="border-chariot-stroke border-b">
                <td className="bg-chariot-background-primary text-chariot-text-secondary w-1/5 px-4 py-3 align-top font-medium">
                  Description
                </td>
                <td className="text-chariot-text-primary px-4 py-3">
                  <div className="prose prose-sm max-w-none">
                    <MarkdownPreview source={description} />
                  </div>
                </td>
              </tr>
              {code_snippet && (
                <tr className="border-chariot-stroke border-b">
                  <td className="bg-chariot-background-primary text-chariot-text-secondary w-1/5 px-4 py-3 align-top font-medium">
                    Code
                  </td>
                  <td className="text-chariot-text-primary px-4 py-3">
                    <pre className="bg-chariot-background-secondary max-h-64 overflow-auto rounded p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap">
                      {code_snippet}
                    </pre>
                  </td>
                </tr>
              )}
              {remediation && (
                <tr className="border-chariot-stroke border-b last:border-b-0">
                  <td className="bg-chariot-background-primary text-chariot-text-secondary w-1/5 px-4 py-3 align-top font-medium">
                    Remediation
                  </td>
                  <td className="text-chariot-text-primary px-4 py-3">
                    <div className="prose prose-sm max-w-none">
                      <MarkdownPreview source={remediation} />
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Verification analysis if available */}
        {verification && (
          <div className="border-chariot-stroke overflow-hidden rounded-lg border">
            <div className="bg-chariot-background-secondary flex items-center justify-between px-4 py-3">
              <h5 className="text-chariot-text-primary text-sm font-medium">
                Verification Analysis
              </h5>
              <span
                className={cn(
                  'inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium',
                  verification.status === 'TRUE_POSITIVE'
                    ? 'bg-chariot-status-critical border-chariot-stroke-critical text-white'
                    : 'bg-chariot-status-info border-chariot-stroke-info text-white'
                )}
              >
                {verification.status?.replace(/_/g, ' ')}
              </span>
            </div>
            <table className="w-full">
              <tbody>
                {verification.reasoning && (
                  <tr className="border-chariot-stroke border-b">
                    <td className="bg-chariot-background-primary text-chariot-text-secondary w-1/5 px-4 py-3 align-top font-medium">
                      Reasoning
                    </td>
                    <td className="text-chariot-text-primary px-4 py-3">
                      <div className="prose prose-sm max-w-none">
                        <MarkdownPreview source={verification.reasoning} />
                      </div>
                    </td>
                  </tr>
                )}
                {verification.exploitability_assessment && (
                  <tr className="border-chariot-stroke border-b">
                    <td className="bg-chariot-background-primary text-chariot-text-secondary w-1/5 px-4 py-3 align-top font-medium">
                      Exploitability
                    </td>
                    <td className="text-chariot-text-primary px-4 py-3">
                      <div className="prose prose-sm max-w-none">
                        <MarkdownPreview
                          source={verification.exploitability_assessment}
                        />
                      </div>
                    </td>
                  </tr>
                )}
                {verification.attack_prerequisites && (
                  <tr className="border-chariot-stroke border-b last:border-b-0">
                    <td className="bg-chariot-background-primary text-chariot-text-secondary w-1/5 px-4 py-3 align-top font-medium">
                      Prerequisites
                    </td>
                    <td className="text-chariot-text-primary px-4 py-3">
                      <div className="prose prose-sm max-w-none">
                        <MarkdownPreview
                          source={verification.attack_prerequisites}
                        />
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Exploit details if available */}
        {exploit && (
          <div className="border-chariot-stroke overflow-hidden rounded-lg border">
            <div className="bg-chariot-background-secondary flex items-center justify-between px-4 py-3">
              <h5 className="text-chariot-text-primary flex items-center gap-2 text-sm font-medium">
                <ShieldExclamationIcon className="size-4" />
                Exploitation Attempt
              </h5>
              <span
                className={cn(
                  'inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium text-white',
                  exploit.status === 'EXPLOITABLE'
                    ? 'bg-chariot-status-critical border-chariot-stroke-critical'
                    : exploit.status === 'COULD_NOT_EXPLOIT'
                      ? 'bg-yellow-500/20 text-yellow-600'
                      : 'bg-chariot-status-info border-chariot-stroke-info'
                )}
              >
                {exploit.status === 'EXPLOITABLE' ? (
                  <>
                    <ExclamationTriangleIcon className="mr-1 size-3" />
                    Exploitable
                  </>
                ) : exploit.status === 'COULD_NOT_EXPLOIT' ? (
                  <>
                    <ExclamationTriangleIcon className="mr-1 size-3" />
                    Inconclusive
                  </>
                ) : (
                  <>
                    <Check className="mr-1 size-3" />
                    Not Exploitable
                  </>
                )}
              </span>
            </div>

            <table className="w-full">
              <tbody>
                {exploit.vulnerability_report?.executive_summary && (
                  <tr className="border-chariot-stroke border-b">
                    <td className="bg-chariot-background-primary text-chariot-text-secondary w-1/5 px-4 py-3 align-top font-medium">
                      Summary
                    </td>
                    <td className="text-chariot-text-primary px-4 py-3">
                      <div className="prose prose-sm max-w-none">
                        <MarkdownPreview
                          source={
                            exploit.vulnerability_report.executive_summary
                          }
                        />
                      </div>
                    </td>
                  </tr>
                )}
                {exploit.reasoning && (
                  <tr className="border-chariot-stroke border-b">
                    <td className="bg-chariot-background-primary text-chariot-text-secondary w-1/5 px-4 py-3 align-top font-medium">
                      Analysis
                    </td>
                    <td className="text-chariot-text-primary px-4 py-3">
                      <div className="prose prose-sm max-w-none">
                        <MarkdownPreview source={exploit.reasoning} />
                      </div>
                    </td>
                  </tr>
                )}
                {exploit.success_indicators &&
                  exploit.success_indicators.length > 0 && (
                    <tr className="border-chariot-stroke border-b">
                      <td className="bg-chariot-background-primary text-chariot-text-secondary w-1/5 px-4 py-3 align-top font-medium">
                        Indicators
                      </td>
                      <td className="text-chariot-text-primary px-4 py-3">
                        <ul className="list-disc space-y-1 pl-4 text-sm">
                          {exploit.success_indicators.map((indicator, i) => (
                            <li key={i}>{indicator}</li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  )}
                {exploit.vulnerability_report?.steps_to_reproduce &&
                  exploit.vulnerability_report.steps_to_reproduce.length >
                    0 && (
                    <tr className="border-chariot-stroke border-b">
                      <td className="bg-chariot-background-primary text-chariot-text-secondary w-1/5 px-4 py-3 align-top font-medium">
                        Steps
                      </td>
                      <td className="text-chariot-text-primary px-4 py-3">
                        <ol className="list-decimal space-y-1 pl-4 text-sm">
                          {exploit.vulnerability_report.steps_to_reproduce.map(
                            (step, i) => (
                              <li key={i}>{step}</li>
                            )
                          )}
                        </ol>
                      </td>
                    </tr>
                  )}
                {(exploit.poc_code ||
                  exploit.vulnerability_report?.proof_of_concept) && (
                  <tr className="border-chariot-stroke border-b">
                    <td className="bg-chariot-background-primary text-chariot-text-secondary w-1/5 px-4 py-3 align-top font-medium">
                      PoC Code
                    </td>
                    <td className="text-chariot-text-primary px-4 py-3">
                      <pre className="bg-chariot-background-secondary max-h-64 overflow-auto rounded p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap">
                        {exploit.poc_code ||
                          exploit.vulnerability_report?.proof_of_concept}
                      </pre>
                    </td>
                  </tr>
                )}
                {exploit.commands_summary && (
                  <tr className="border-chariot-stroke border-b last:border-b-0">
                    <td className="bg-chariot-background-primary text-chariot-text-secondary w-1/5 px-4 py-3 align-top font-medium">
                      Commands
                    </td>
                    <td className="text-chariot-text-primary px-4 py-3">
                      <pre className="bg-chariot-background-secondary max-h-32 overflow-auto rounded p-2 font-mono text-xs whitespace-pre-wrap">
                        {exploit.commands_summary}
                      </pre>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Additional data — fields not explicitly rendered above */}
        {(() => {
          const extraFindingData = Object.fromEntries(
            Object.entries(finding).filter(
              ([k]) => !RENDERED_FINDING_KEYS.has(k)
            )
          );
          const extraExploitData = exploit
            ? Object.fromEntries(
                Object.entries(exploit).filter(
                  ([k]) => !RENDERED_EXPLOIT_KEYS.has(k)
                )
              )
            : {};
          const hasExtra =
            Object.keys(extraFindingData).length > 0 ||
            Object.keys(extraExploitData).length > 0;
          if (!hasExtra) return null;
          return (
            <details className="border-chariot-stroke overflow-hidden rounded-lg border">
              <summary className="bg-chariot-background-secondary text-chariot-text-secondary cursor-pointer px-4 py-3 text-sm font-medium">
                Additional Data
              </summary>
              <pre className="bg-chariot-background-primary max-h-96 overflow-auto p-4 font-mono text-xs leading-relaxed">
                {JSON.stringify(
                  {
                    ...(Object.keys(extraFindingData).length > 0
                      ? { finding: extraFindingData }
                      : {}),
                    ...(Object.keys(extraExploitData).length > 0
                      ? { exploit: extraExploitData }
                      : {}),
                  },
                  null,
                  2
                )}
              </pre>
            </details>
          );
        })()}
      </div>
    );

    tempTabs['DETECTION'].push({
      label: `${id}: ${title.substring(0, 100)}${title.length > 100 ? '...' : ''}`,
      value: tabId,
    });
  });
};
